import { useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetExternalApiKeyQueryKey,
  useGenerateExternalApiKey,
} from "~/api/organizations/organizations.ts";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useSettingsParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

interface CreateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateApiKeyDialog: FC<CreateApiKeyDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { organizationId } = useSettingsParams();
  const queryClient = useQueryClient();
  const { mutateAsync: createApiKey, isPending } = useGenerateExternalApiKey();
  const { toast } = useToast();
  const intl = useIntl();
  const [name, setName] = useState("");
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "settings.developers.apiKeyNameRequired",
          defaultMessage: "API key name is required",
        }),
      });
      return;
    }

    try {
      const response = await createApiKey({
        organizationId,
        data: { name: name.trim() },
      });

      // Store the created API key to show it
      setCreatedApiKey(response.apiKey);

      // Update the query cache - invalidate to refetch the full list
      queryClient.invalidateQueries({
        queryKey: getGetExternalApiKeyQueryKey(organizationId),
      });

      trackEvent({
        eventName: "app/settings_developers/create_api_key.success",
      });

      setName("");
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "settings.developers.createApiKeyError",
          defaultMessage: "Failed to create API key",
        }),
      });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setName("");
    setCreatedApiKey(null);
  };

  const handleCopyKey = () => {
    if (!createdApiKey) return;

    trackEvent({
      eventName: "app/settings_developers/copy_key.button/click",
    });

    navigator.clipboard.writeText(createdApiKey);
    toast({
      title: intl.formatMessage({
        id: "settings.developers.keyCopied",
        defaultMessage: "API key copied to clipboard",
      }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="settings.developers.createApiKeyDialog.title"
              defaultMessage="Create API Key"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="settings.developers.createApiKeyDialog.description"
              defaultMessage="Enter a name for your new API key. This will help you identify it later."
            />
          </DialogDescription>
        </DialogHeader>
        {createdApiKey ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                <FormattedMessage
                  id="settings.developers.apiKeyCreated"
                  defaultMessage="Your API Key"
                />
              </Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-4 bg-muted rounded-lg">
                  <code className="text-sm font-mono break-all">
                    {createdApiKey}
                  </code>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={handleCopyKey}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="settings.developers.apiKeyWarning"
                  defaultMessage="Please copy this key now. You won't be able to see it again."
                />
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="create-api-key-form">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api-key-name">
                  <FormattedMessage
                    id="settings.developers.apiKeyName"
                    defaultMessage="API Key Name"
                  />
                </Label>
                <Input
                  id="api-key-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: "settings.developers.apiKeyNamePlaceholder",
                    defaultMessage: "e.g., Production Server",
                  })}
                  className="shadow-none"
                  autoFocus
                  required
                />
              </div>
            </div>
          </form>
        )}
        <DialogFooter>
          {createdApiKey ? (
            <Button onClick={handleClose}>
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                type="submit"
                form="create-api-key-form"
                disabled={isPending}
              >
                <FormattedMessage id="create" defaultMessage="Create" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
